<template>
  <v-row no-gutters>
    <v-col class="px-1" v-if="isFetched" cols="6">
      <v-row no-gutters class="pb-4" justify="space-between">
        <v-col cols="auto">
          <base-button translation-path="button.addPattern" @click="openPatternDialog(undefined)"></base-button>
        </v-col>
        <v-col cols="5">
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            class="searchInput"
          >
          </data-input>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12">
          <v-list class="pattern-list" dense>
            <v-list-item
              v-for="pattern in searchedItems"
              :key="pattern.id"
              class="pattern-element px-2"
              :ripple="false"
            >
              <v-list-item-content @click="openPatternDialog(pattern)">
                <v-list-item-title>
                  <v-tooltip bottom color="font" max-width="300">
                    <template #activator="{ on }">
                      <div v-on="on" class="text-body-2 font-weight-regular text-truncate py-0">
                        {{ pattern.name }}
                      </div>
                    </template>
                    <div style="word-break: break-all;">
                      {{ pattern.name }}
                    </div>
                  </v-tooltip>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <dots-menu
                  font-size="19px"
                  :items="[{ name: 'delete' }]"
                  @delete="openDeleteDialog(pattern.id)"
                ></dots-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else>
      <main-loader></main-loader>
    </v-col>
    <LanternPatternDialog
      v-if="isPatternDialogLoaded"
      :is-visible.sync="isPatternDialogVisible"
      :pattern="editingPattern"
      :pole-type-features="poleTypeFeatures"
      :head-type-features="headTypeFeatures"
      :pick-arm-features="pickArmFeatures"
      @modalClosed="resetData"
    ></LanternPatternDialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.deletePattern')"
      :body="$i18n.t('dialog.body.deletePatternConfirmation')"
      agree-text="dialog.agreeDelete"
      :loading="isDeleteLoading"
      @agree="handleDeletePattern"
    ></main-dialog>
  </v-row>
</template>

<script>
import BaseButton from '@/components/BaseButton';
import { call, get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
export default {
  name: 'ZDMLanternPatternsTab',
  components: {
    DataInput,
    MainLoader,
    MainDialog: () => import('@/components/MainDialog'),
    DotsMenu,
    LanternPatternDialog: () => import('@/components/LanternPatternDialog'),
    BaseButton,
  },
  data: () => ({
    search: '',
    editingPattern: undefined,
    deleteId: null,
    isPatternDialogLoaded: false,
    isDeleteLoading: false,
    isPatternDialogVisible: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isFetched: false,
  }),
  computed: {
    lanternPatterns: get('zdm_data/lanternPatterns'),
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    searchedItems() {
      if (!this.search) {
        return this.lanternPatterns;
      }
      return this.lanternPatterns.filter(a => a.name.toLowerCase().includes(this.search.toLowerCase()));
    },
    poleTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pole'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    pickArmFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pick_arm'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    headTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_head'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getPatterns: call('zdm_data/getPatterns'),
    deletePattern: call('zdm_data/deletePattern'),
    openDeleteDialog(id) {
      this.deleteId = id;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    openPatternDialog(c) {
      if (c) {
        this.isPatternDialogLoaded = true;
        this.isPatternDialogVisible = true;
        this.editingPattern = c;
      } else {
        this.isPatternDialogLoaded = true;
        this.isPatternDialogVisible = true;
        this.editingPattern = {};
      }
    },
    async handleDeletePattern() {
      this.isDeleteLoading = true;
      try {
        await this.deletePattern(this.deleteId);
      } catch (e) {
        console.log(e);
      } finally {
        this.isDeleteDialogLoaded = false;
        this.isDeleteDialogVisible = false;
        this.isDeleteLoading = false;
      }
    },
    resetData() {
      this.editingPattern = undefined;
      this.isPatternDialogLoaded = false;
      this.isPatternDialogVisible = false;
    },
    async getFeatures(datasource) {
      if (!this.dataSourcesFeatures[datasource]) {
        await this.getDataSourceFeatures({
          dataSource: datasource,
        });
      }
    },
    async init() {
      await Promise.all([
        this.getFeatures('zdm_data_catalog_pole'),
        this.getFeatures('zdm_data_catalog_pick_arm'),
        this.getFeatures('zdm_data_catalog_head'),
        this.getPatterns(),
      ]);
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.pattern-list {
  padding: 0;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 0px rgba(0, 0, 0, 0.15) !important;
}
.pattern-element {
  @include border(bottom);
  text-align: left;
  min-height: 36px;
  max-height: 36px;
  &:hover {
    background-color: rgba(188, 188, 188, 0.2);
    cursor: pointer;
  }
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
